// Оркестратор (docs/orchestrator-spec.md): голосовой ассистент панели.
// Боковое меню — «Новый разговор», прошлые разговоры из журнала сервера и
// заметка персоны; рабочая область — общий Panel (круг, кнопки, лента).
// keepAlive: разговор держит WebSocket и микрофон, уход во вкладку соседа
// его рвать не должен (а сам стор живёт на уровне модуля).

import { useEffect } from 'react'
import { MOBILE_QUERY, useMediaQuery } from '../../lib/useMediaQuery'
import { useAppStore } from '../../store/appStore'
import { SideMenu } from '../../ui/SideMenu'
import type { SectionModule } from '../types'
import { OrchestratorPanel } from './Panel'
import { isLive, PERSONA_NOTE, statusText, useOrchestrator } from './store'

/** Беседа, которой оркестратор поручил задачу, — открыть в «Беседах». */
export async function openAgentSession(sessionId: string): Promise<void> {
  const app = useAppStore.getState()
  // беседа могла появиться только что — без свежего списка окно её не найдёт
  if (!app.sessions.some((s) => s.id === sessionId)) await app.refreshSessions().catch(() => undefined)
  useAppStore.getState().focusSession(sessionId)
}

function Sidebar() {
  const history = useOrchestrator((s) => s.history)
  const historyError = useOrchestrator((s) => s.historyError)
  const viewing = useOrchestrator((s) => s.viewing)
  const status = useOrchestrator((s) => s.status)
  const muted = useOrchestrator((s) => s.muted)
  const autoMuted = useOrchestrator((s) => s.autoMuted)
  const { newConversation, openHistory, closeHistory, loadHistory } = useOrchestrator.getState()
  const openNote = useAppStore((s) => s.openNote)
  const persona = useOrchestrator((s) => s.key?.personaPath) || PERSONA_NOTE
  const live = isLive(status)

  useEffect(() => {
    void loadHistory()
  }, [loadHistory])

  return (
    <SideMenu
      screen="orchestrator"
      sections={[
        {
          id: 'talk',
          title: 'Разговор',
          onAdd: newConversation,
          addTitle: 'новый разговор',
          items: [
            ...(live ? [{
              id: 'current',
              label: 'текущий разговор',
              live: true,
              active: viewing === null,
              meta: statusText(status, '', muted, autoMuted),
              metaTone: 'ok' as const,
              onSelect: closeHistory,
            }] : []),
            { id: 'new', label: '+ Новый разговор', action: true, onSelect: newConversation },
          ],
        },
        {
          id: 'history',
          title: 'Прошлые разговоры',
          count: history.length ? String(history.length) : undefined,
          items: historyError
            ? [{ id: 'err', label: 'журнал недоступен', title: historyError, action: true, onSelect: () => void loadHistory() }]
            : history.length === 0
              ? [{ id: 'none', label: 'пока пусто', action: true }]
              : history.map((h) => ({
                id: h.id,
                label: h.label,
                title: `${h.when} — ${h.label}`,
                meta: h.when,
                metaTone: 'dim' as const,
                active: viewing === h.id,
                onSelect: () => void openHistory(h.id),
              })),
        },
        {
          id: 'persona',
          title: 'Персона',
          items: [{
            id: 'assistant',
            label: 'Ассистент',
            meta: 'в Базе',
            metaTone: 'link' as const,
            title: `характер ассистента — заметка ${persona}`,
            onSelect: () => openNote(persona),
          }],
        },
      ]}
    />
  )
}

function View() {
  // на узком экране та же оболочка — круг и кнопки в телефонном размере
  const phone = useMediaQuery(MOBILE_QUERY)
  return <OrchestratorPanel compact={phone} onOpenSession={(id) => void openAgentSession(id)} />
}

function useRail() {
  const status = useOrchestrator((s) => s.status)
  const muted = useOrchestrator((s) => s.muted)
  const autoMuted = useOrchestrator((s) => s.autoMuted)
  const inbox = useOrchestrator((s) => s.key?.inbox ?? 0)
  if (isLive(status)) return { count: '●', tip: `оркестратор: ${statusText(status, '', muted, autoMuted)}` }
  return inbox
    ? { count: String(inbox), tip: `обновлений рабочих бесед: ${inbox}` }
    : { tip: 'оркестратор выключен' }
}

const orchestrator: SectionModule = { Sidebar, View, keepAlive: true, useRail }
export default orchestrator
